import type { FC } from "react"
import { useTranslation } from "react-i18next"

import { AuthenticatedPageLayout } from "@/components/authenticated-page-layout"
import { useAuthentication } from "@/contexts"
import type { Lab } from "@/models/lab"
import { useFlags } from "@/services/feature"

import ScienceIcon from "@mui/icons-material/Science"
import { Box, Skeleton, Stack, Typography, useTheme } from "@mui/material"

import { LabCard } from "../../components/lab-card/lab-card"
import { useGetLabs } from "../../services/get-labs"

const isLabVisible = (
  lab: Lab,
  isAdminUser: boolean,
  flagsData: Record<string, boolean> | undefined
): boolean => {
  // Impersonation changes the active role only. The API stays signed in as
  // the admin, so an internal lab can still be in this list.
  if (lab.audience === "internal" && !isAdminUser) {
    return false
  }

  if (isAdminUser || !lab.featureFlag) {
    return true
  }

  return flagsData?.[lab.featureFlag] === true
}

const LabsEmptyState: FC = () => {
  const { t } = useTranslation("common")
  const theme = useTheme()

  return (
    <Stack
      sx={{
        alignItems: "center",
        flex: 1,
        justifyContent: "center",
        textAlign: "center",
      }}
    >
      <ScienceIcon
        aria-hidden
        sx={{
          color: "grey.200",
          fontSize: theme.typography.pxToRem(128),
        }}
      />
      <Typography component="h2" variant="h1">
        {t("labs.empty")}
      </Typography>
      <Typography>{t("labs.checkBackLater")}</Typography>
    </Stack>
  )
}

interface LabsCatalogSkeletonProps {
  count?: number
}

const LabsCatalogSkeleton: FC<LabsCatalogSkeletonProps> = ({ count = 3 }) => {
  const { t } = useTranslation("common")

  return (
    <Stack
      aria-busy
      aria-label={t("loadingAriaLabel", {
        title: t("moduleTitle.labs"),
      })}
      direction="row"
      role="status"
      sx={{
        flexWrap: "wrap",
        gap: 2,
      }}
    >
      {Array.from({ length: count }, (_, index) => (
        <Skeleton
          data-testid="lab-card-skeleton"
          key={index}
          sx={{
            height: (theme) => theme.typography.pxToRem(212),
            width: (theme) => theme.typography.pxToRem(320),
          }}
          variant="rounded"
        />
      ))}
    </Stack>
  )
}

interface LabsCatalogProps {
  labs: Lab[]
  showEmpty: boolean
}

const LabsCatalog: FC<LabsCatalogProps> = ({ labs, showEmpty }) => {
  if (showEmpty) {
    return <LabsEmptyState />
  }

  return (
    <Stack
      direction="row"
      sx={{
        flexWrap: "wrap",
        gap: 2,
      }}
    >
      {labs.map((lab) => (
        <LabCard key={lab.id} lab={lab} />
      ))}
    </Stack>
  )
}

export const LabsPage: FC = () => {
  const { t } = useTranslation("common")
  const { isAdminUser } = useAuthentication()
  const flags = useFlags()
  const { labsData, labsIsLoading, labsIsSuccess } = useGetLabs()
  const visibleLabs = (labsData ?? []).filter((lab) =>
    isLabVisible(lab, isAdminUser, flags.flagsData)
  )
  const showEmpty = labsIsSuccess && visibleLabs.length === 0

  return (
    <AuthenticatedPageLayout
      fullHeight={showEmpty}
      title={t("moduleTitle.labs")}
    >
      <Stack
        sx={{
          height: showEmpty ? "100%" : undefined,
        }}
      >
        <Box
          sx={{
            mb: 2,
          }}
        >
          <Typography>{t("labs.description")}</Typography>
        </Box>
        {labsIsLoading ? <LabsCatalogSkeleton /> : null}
        {labsIsSuccess ? (
          <LabsCatalog labs={visibleLabs} showEmpty={showEmpty} />
        ) : null}
      </Stack>
    </AuthenticatedPageLayout>
  )
}
